'use client';

import { useAuth } from '@/lib/auth-client';
import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'pitchcrow-recent-spaces';
const MAX_RECENT = 5;
const UPDATED_EVENT = 'pitchcrow-recent-spaces-updated';

export interface RecentSpace {
  id: string;
  name: string;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

function read(userId: string): RecentSpace[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is RecentSpace =>
        !!p && typeof p.id === 'string' && typeof p.name === 'string'
    );
  } catch {
    return [];
  }
}

export function useAddRecentSpace(): (space: RecentSpace) => void {
  const { user } = useAuth();
  const userId = user?.id;

  return useCallback(
    (space: RecentSpace) => {
      if (!userId) return;
      const next = [
        space,
        ...read(userId).filter((p) => p.id !== space.id),
      ].slice(0, MAX_RECENT);
      try {
        localStorage.setItem(storageKey(userId), JSON.stringify(next));
        window.dispatchEvent(new Event(UPDATED_EVENT));
      } catch {
        // ignore quota / serialization errors
      }
    },
    [userId]
  );
}

export function useRecentSpaces(): RecentSpace[] {
  const { user } = useAuth();
  const userId = user?.id;
  const [recent, setRecent] = useState<RecentSpace[]>([]);

  const refresh = useCallback(() => {
    setRecent(userId ? read(userId) : []);
  }, [userId]);

  useEffect(() => {
    refresh();
    window.addEventListener(UPDATED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(UPDATED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  return recent;
}
